import * as React from 'react';

import type {
    CalendarSurfaceItemProps,
    CalendarSurfaceItemStyleProps,
    CalendarSurfaceItemStyles,
} from './CalendarSurfaceItem.types';
import {
    getCalendarEventItemAriaLabel,
    getCalendarEventItemTitle,
} from 'owa-calendar-event-attributes';
import { getItemWrapperClass, renderFreeBusyBar } from '../CalendarSurfaceItemComponentRenderer';

import type { CalendarEvent } from 'owa-calendar-types';
import TimeConstants from 'owa-datetime-utils/lib/TimeConstants';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { differenceInMilliseconds } from 'owa-datetime';
import { getUserOptions } from 'owa-session-store';
import { observer } from 'mobx-react-lite';
import { isFeatureEnabled } from 'owa-feature-flags';
import { useBooleanState } from 'owa-react-hooks/lib/useBooleanState';

import surfaceItemStyles from '../CalendarSurfaceItem.scss';
import classNames from 'classnames';

const getClassNames = classNamesFunction<
    CalendarSurfaceItemStyleProps,
    CalendarSurfaceItemStyles
>();

/**
 * Template for calendar items on day/week view
 */
export default observer(function CalendarSurfaceItemBase(props: CalendarSurfaceItemProps) {
    const [hover, setHover, unsetHover] = useBooleanState(false);
    let {
        freeBusyType,
        colorScheme,
        charmId,
        hideFreeBusyBar,
        className,
        ariaLabel,
        title,
        disableTabIndex,
        onRender,
        responseType,
        isCancelled,
        hideBorder,
        theme,
        styles,
        duration,
        item,
        itemState,
        allowEmptySubject,
        increaseFontSize,
        joinButtonVisible,
        shouldHideTooltip,
    } = props;
    if (item) {
        // assign properties that come from the item if they're undefined- check undefined explicitly in case of falsy/zero values
        freeBusyType = freeBusyType != undefined ? freeBusyType : item.FreeBusyType;
        responseType = responseType != undefined ? responseType : item.ResponseType;
        duration =
            duration != undefined ? duration : differenceInMilliseconds(item.End, item.Start);
    }
    const useBoldTheme = getUserOptions().UseBoldCalendarColorThemeReact;
    hideFreeBusyBar = hideFreeBusyBar && !useBoldTheme; // never hide FB bars with bold theme
    const classes = getClassNames(styles, {
        theme: theme!,
        className,
        freeBusyType,
        responseType,
        colorScheme,
        charmId,
        isCancelled: isCancelled || responseType == 'Decline',
        hideBorder,
        useBoldTheme: useBoldTheme,
    });
    const childrenProps = {
        contentClass: classes.content,
        item,
        colorScheme,
        charmId,
        itemState,
        allowEmptySubject,
        joinButtonVisible,
    };

    const getTooltip = (item: CalendarEvent) => {
        if (hover && shouldHideTooltip?.()) {
            return null;
        }

        if (title !== undefined || !item) {
            return title;
        }

        return getCalendarEventItemTitle(item, timeZoneId);
    };

    const timeZoneId = itemState?.timeZoneId;
    ariaLabel = ariaLabel || !item ? ariaLabel : getCalendarEventItemAriaLabel(item, timeZoneId);
    className = className || !item ? className : getItemWrapperClass(item);

    const shouldShortenEventBox = isFeatureEnabled('cal-mf-shortenEventBoxSurface');
    const minimumDuration = shouldShortenEventBox
        ? TimeConstants.MillisecondsIn15Mins
        : TimeConstants.MillisecondsIn30Mins;

    const contentWrapperStyles = classNames(surfaceItemStyles.contentWrapper, {
        [surfaceItemStyles.shortItem]:
            shouldShortenEventBox && duration < TimeConstants.MillisecondsIn30Mins,
        [surfaceItemStyles.shortestItem]:
            shouldShortenEventBox && duration < TimeConstants.MillisecondsIn24Mins,
    });

    // content info is in the aria-label, aria-hide the rest to avoid content duplication in scan mode
    return (
        <div
            role={disableTabIndex ? undefined : 'button'}
            className={classNames(classes.root, surfaceItemStyles.wrapperDiv, className, {
                [surfaceItemStyles.fullFBBar]: duration >= minimumDuration,
                [surfaceItemStyles.wrapperDivHiddenFB]: hideFreeBusyBar,
                [surfaceItemStyles.increaseFontSize]: increaseFontSize,
            })}
            tabIndex={disableTabIndex ? undefined : 0}
            aria-label={ariaLabel}
            onMouseOver={setHover}
            onMouseLeave={unsetHover}
            title={getTooltip(item)}>
            {!hideFreeBusyBar && renderFreeBusyBar(freeBusyType, duration, colorScheme)}
            <div
                className={classNames(surfaceItemStyles.colorWrapper, classes.content)}
                aria-hidden={true}>
                <div className={contentWrapperStyles}>{onRender?.(childrenProps)}</div>
            </div>
        </div>
    );
});
